import { TrendingUp, TrendingDown, Minus, Waves, ExternalLink, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useWhaleSummaries, useWhaleSummary, useTrackWhaleMovements, getSentimentColor, getSentimentLabel, type WhaleSummary } from '@/hooks/useWhaleTracking';

interface WhaleFlowProps {
  symbol: string;
  volume24h: number;
  volumeChange?: number;
  marketCap: number;
  priceChange24h?: number;
  priceChange7d?: number;
}

interface WhaleSignal {
  flow: 'accumulating' | 'distributing' | 'neutral';
  strength: 'strong' | 'moderate' | 'weak';
  confidence: number;
  description: string;
}

function calculateWhaleFlow(
  volume24h: number,
  marketCap: number,
  priceChange24h?: number,
  priceChange7d?: number,
  onChainData?: WhaleSummary | null
): WhaleSignal {
  // If we have on-chain data, use it for more accurate signals
  if (onChainData) {
    const sentiment = onChainData.whale_sentiment;
    let flow: 'accumulating' | 'distributing' | 'neutral' = 'neutral';
    let strength: 'strong' | 'moderate' | 'weak' = 'weak';
    let confidence = 50;
    let description = 'Based on real blockchain data';

    if (sentiment === 'strong_accumulation') {
      flow = 'accumulating';
      strength = 'strong';
      confidence = 90;
      description = `Whales accumulated $${formatLargeNumber(Math.abs(onChainData.net_flow_usd))} - ${onChainData.accumulation_count} buy transactions detected`;
    } else if (sentiment === 'accumulation') {
      flow = 'accumulating';
      strength = 'moderate';
      confidence = 75;
      description = `Net accumulation of $${formatLargeNumber(Math.abs(onChainData.net_flow_usd))} by whale wallets`;
    } else if (sentiment === 'strong_distribution') {
      flow = 'distributing';
      strength = 'strong';
      confidence = 90;
      description = `Whales distributed $${formatLargeNumber(Math.abs(onChainData.net_flow_usd))} - ${onChainData.distribution_count} sell transactions detected`;
    } else if (sentiment === 'distribution') {
      flow = 'distributing';
      strength = 'moderate';
      confidence = 75;
      description = `Net distribution of $${formatLargeNumber(Math.abs(onChainData.net_flow_usd))} by whale wallets`;
    } else {
      description = `Balanced whale activity: ${onChainData.accumulation_count} buys, ${onChainData.distribution_count} sells`;
    }

    return { flow, strength, confidence, description };
  }

  // Fallback to volume-based analysis
  const volumeToMcapRatio = volume24h / marketCap;
  const price24h = priceChange24h || 0;
  const price7d = priceChange7d || 0;
  
  let flow: 'accumulating' | 'distributing' | 'neutral' = 'neutral';
  let strength: 'strong' | 'moderate' | 'weak' = 'weak';
  let confidence = 50;
  let description = 'Normal trading activity';
  
  const isHighVolume = volumeToMcapRatio > 0.15;
  const isModerateVolume = volumeToMcapRatio > 0.08;
  
  const priceMovingUp = price24h > 2 && price7d > 0;
  const priceMovingDown = price24h < -2 && price7d < 0;
  const priceSteadyUp = price24h > 0 && price7d > 5;
  const priceSteadyDown = price24h < 0 && price7d < -5;
  
  if (isHighVolume && priceMovingUp) {
    flow = 'accumulating';
    strength = 'strong';
    confidence = 85;
    description = 'Smart money likely accumulating - high volume with rising prices';
  } else if (isModerateVolume && priceSteadyUp) {
    flow = 'accumulating';
    strength = 'moderate';
    confidence = 70;
    description = 'Steady accumulation pattern detected';
  } else if (isHighVolume && price24h > 0 && price24h < 5) {
    flow = 'accumulating';
    strength = 'weak';
    confidence = 55;
    description = 'Possible accumulation - volume elevated with slight price gain';
  } else if (isHighVolume && priceMovingDown) {
    flow = 'distributing';
    strength = 'strong';
    confidence = 85;
    description = 'Smart money likely distributing - high volume with falling prices';
  } else if (isModerateVolume && priceSteadyDown) {
    flow = 'distributing';
    strength = 'moderate';
    confidence = 70;
    description = 'Steady distribution pattern detected';
  } else if (isHighVolume && price24h < 0 && price24h > -5) {
    flow = 'distributing';
    strength = 'weak';
    confidence = 55;
    description = 'Possible distribution - volume elevated with slight price drop';
  } else if (!isModerateVolume && Math.abs(price24h) < 3) {
    flow = 'neutral';
    strength = 'moderate';
    confidence = 65;
    description = 'Consolidation phase - low volume, stable price';
  }
  
  return { flow, strength, confidence, description };
}

function formatLargeNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
  return num.toFixed(0);
}

export function WhaleFlowIndicator({ 
  symbol, 
  volume24h, 
  marketCap, 
  priceChange24h,
  priceChange7d 
}: WhaleFlowProps) {
  const { data: onChainData } = useWhaleSummary(symbol);
  const signal = calculateWhaleFlow(volume24h, marketCap, priceChange24h, priceChange7d, onChainData);
  
  const getFlowIcon = () => {
    switch (signal.flow) {
      case 'accumulating':
        return <TrendingUp className="w-4 h-4" />;
      case 'distributing':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };
  
  const getFlowColor = () => {
    switch (signal.flow) {
      case 'accumulating':
        return signal.strength === 'strong' 
          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
          : 'bg-green-500/10 text-green-400/80 border-green-500/20';
      case 'distributing':
        return signal.strength === 'strong'
          ? 'bg-red-500/20 text-red-400 border-red-500/30'
          : 'bg-red-500/10 text-red-400/80 border-red-500/20';
      default:
        return 'bg-muted/50 text-muted-foreground border-border';
    }
  };
  
  const getFlowLabel = () => {
    const strengthLabel = signal.strength === 'strong' ? '🔥 ' : signal.strength === 'moderate' ? '⚡ ' : '';
    switch (signal.flow) {
      case 'accumulating':
        return `${strengthLabel}Accumulating`;
      case 'distributing':
        return `${strengthLabel}Distributing`;
      default:
        return 'Neutral';
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`gap-1 cursor-help ${getFlowColor()}`}
          >
            <Waves className="w-3 h-3" />
            {getFlowIcon()}
            {getFlowLabel()}
            {onChainData && <span className="text-[10px] opacity-70">⛓️</span>}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-2">
            <p className="font-semibold flex items-center gap-1">
              Whale Flow Analysis
              {onChainData && <span className="text-xs text-primary">(On-Chain Data)</span>}
            </p>
            <p className="text-sm">{signal.description}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Confidence: {signal.confidence}%</span>
              <span>•</span>
              <span>Vol/MCap: {((volume24h / marketCap) * 100).toFixed(1)}%</span>
            </div>
            {onChainData && (
              <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
                Last updated: {new Date(onChainData.analyzed_at).toLocaleString()}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Compact version for inline use
export function WhaleFlowBadge({ 
  symbol,
  volume24h, 
  marketCap, 
  priceChange24h,
  priceChange7d 
}: WhaleFlowProps) {
  const { data: onChainData } = useWhaleSummary(symbol || '');
  const signal = calculateWhaleFlow(volume24h, marketCap, priceChange24h, priceChange7d, onChainData);
  
  // Only show whale badge when we have on-chain data OR strong heuristic confidence
  const isOnChain = !!onChainData;
  const isHighConfidenceHeuristic = !isOnChain && signal.confidence >= 80 && signal.strength === 'strong';
  
  if (signal.flow === 'neutral' || (!isOnChain && !isHighConfidenceHeuristic)) {
    return null;
  }
  
  const getFlowIcon = () => {
    switch (signal.flow) {
      case 'accumulating':
        return <TrendingUp className="w-3 h-3" />;
      case 'distributing':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return null;
    }
  };
  
  const getFlowColor = () => {
    if (signal.flow === 'accumulating') {
      return signal.strength === 'strong' 
        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
        : 'bg-green-500/10 text-green-400/80 border-green-500/20';
    }
    return signal.strength === 'strong'
      ? 'bg-red-500/20 text-red-400 border-red-500/30'
      : 'bg-red-500/10 text-red-400/80 border-red-500/20';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`gap-1 text-xs cursor-help ${getFlowColor()}`}
          >
            {getFlowIcon()}
          {signal.flow === 'accumulating' ? 'Whales Buying' : 'Whales Selling'}
            {isOnChain && <span className="text-[10px] opacity-70">⛓️</span>}
            {!isOnChain && <span className="text-[10px] opacity-70">📊</span>}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{signal.description}</p>
          <p className="text-xs text-muted-foreground">Confidence: {signal.confidence}%</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Summary widget for homepage showing real whale data
export function WhaleFlowSummary() {
  const { data: summaries, isLoading } = useWhaleSummaries();
  const { mutate: trackWhales, isPending } = useTrackWhaleMovements();

  const accumulatingTokens = summaries?.filter(s => 
    s.whale_sentiment === 'accumulation' || s.whale_sentiment === 'strong_accumulation'
  ) || [];
  
  const distributingTokens = summaries?.filter(s => 
    s.whale_sentiment === 'distribution' || s.whale_sentiment === 'strong_distribution'
  ) || [];

  return (
    <Card className="bg-gradient-to-br from-card/80 via-card/60 to-card/80 backdrop-blur border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Waves className="w-5 h-5 text-primary" />
            Whale Flow Tracker
            <Badge variant="outline" className="text-[10px] ml-1">On-Chain</Badge>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => trackWhales()}
            disabled={isPending}
          >
            <RefreshCw className={`w-4 h-4 ${isPending ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : summaries && summaries.length > 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Real-time whale wallet tracking via Etherscan blockchain data.
            </p>
            
            {/* Accumulating tokens */}
            {accumulatingTokens.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-green-400">
                  <TrendingUp className="w-4 h-4" />
                  Whales Accumulating
                </div>
                <div className="flex flex-wrap gap-2">
                  {accumulatingTokens.map(token => (
                    <Badge 
                      key={token.id} 
                      variant="outline"
                      className="bg-green-500/10 text-green-400 border-green-500/20"
                    >
                      {token.token_symbol}
                      <span className="ml-1 text-xs opacity-70">
                        +${formatLargeNumber(token.net_flow_usd)}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Distributing tokens */}
            {distributingTokens.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-red-400">
                  <TrendingDown className="w-4 h-4" />
                  Whales Distributing
                </div>
                <div className="flex flex-wrap gap-2">
                  {distributingTokens.map(token => (
                    <Badge 
                      key={token.id} 
                      variant="outline"
                      className="bg-red-500/10 text-red-400 border-red-500/20"
                    >
                      {token.token_symbol}
                      <span className="ml-1 text-xs opacity-70">
                        -${formatLargeNumber(Math.abs(token.net_flow_usd))}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* All tokens summary */}
            <div className="grid grid-cols-3 gap-2 text-center mt-4">
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="text-xl font-bold text-green-400">{accumulatingTokens.length}</div>
                <span className="text-xs text-green-400/80">Accumulating</span>
              </div>
              <div className="p-2 rounded-lg bg-muted/50 border border-border">
                <div className="text-xl font-bold text-muted-foreground">
                  {summaries.filter(s => s.whale_sentiment === 'neutral').length}
                </div>
                <span className="text-xs text-muted-foreground">Neutral</span>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="text-xl font-bold text-red-400">{distributingTokens.length}</div>
                <span className="text-xs text-red-400/80">Distributing</span>
              </div>
            </div>

            {summaries[0] && (
              <p className="text-xs text-muted-foreground text-center">
                Last updated: {new Date(summaries[0].analyzed_at).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Track whale wallet movements on Ethereum. Click refresh to fetch latest data.
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                <TrendingUp className="w-5 h-5 mx-auto text-green-400 mb-1" />
                <span className="text-xs text-green-400">Accumulating</span>
              </div>
              <div className="p-2 rounded-lg bg-muted/50 border border-border">
                <Minus className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                <span className="text-xs text-muted-foreground">Neutral</span>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <TrendingDown className="w-5 h-5 mx-auto text-red-400 mb-1" />
                <span className="text-xs text-red-400">Distributing</span>
              </div>
            </div>
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => trackWhales()}
              disabled={isPending}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isPending ? 'animate-spin' : ''}`} />
              Fetch Whale Data
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
